import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STATUS_STYLE = (s) =>
  s === "Done" || s === "Completed" ? "bg-emerald-100 text-emerald-700"
  : s === "In progress" ? "bg-blue-100 text-blue-700"
  : s === "Blocked" ? "bg-red-100 text-red-700"
  : "bg-muted text-muted-foreground";

// Shows a record's "child" collections (phases, line items, sub-tasks) inline,
// ordered by sequence, with status + checklist progress + inline add. Renders
// nothing for records that have no work-item children.
export function ChildRecordsInline({ recordId }) {
  const [groups, setGroups] = useState([]);
  const [adding, setAdding] = useState({}); // rel_def_id -> draft text
  const nav = useNavigate();

  const load = () =>
    api.get(`/records/${recordId}/relationships`).then((r) => setGroups(r.data.groups || [])).catch(() => {});
  useEffect(() => { load(); }, [recordId]);

  const childGroups = groups.filter((g) =>
    (g.items || []).some((it) => it.fields &&
      (it.fields.status !== undefined || it.fields.sequence !== undefined || Array.isArray(it.fields.checklist))));

  if (!childGroups.length) return null;

  const today = new Date(new Date().toDateString());
  const addChild = async (grp) => {
    const text = (adding[grp.rel_def_id] || "").trim();
    if (!text) return;
    try {
      const created = await api.post(`/entity-types/${grp.target_entity_type_id}/records`, { fields: { title: text } });
      await api.post(`/records/${created.data.id}/relationships`, {
        rel_def_id: grp.rel_def_id, target_record_id: recordId,
      });
      setAdding((a) => ({ ...a, [grp.rel_def_id]: "" }));
      load();
    } catch (e) { toast.error("Couldn't add"); }
  };

  return (
    <div className="space-y-4 mb-4">
      {childGroups.map((grp) => {
        const items = [...grp.items].sort((a, b) => ((a.fields?.sequence ?? 9999) - (b.fields?.sequence ?? 9999)));
        return (
          <Card key={grp.rel_def_id + grp.direction} data-testid={`children-${grp.target_entity_type_id}`}>
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base flex items-center gap-2">
                {grp.target_entity_type_name || grp.label}
                <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="divide-y divide-border">
                {items.map((it) => {
                  const f = it.fields || {};
                  const cl = Array.isArray(f.checklist) ? f.checklist : null;
                  const done = cl ? cl.filter((c) => c && c.done).length : 0;
                  const overdue = f.due_date && !["Done", "Completed"].includes(f.status) && new Date(f.due_date) < today;
                  return (
                    <button key={it.id} onClick={() => nav(`/records/${it.id}`)}
                      className="w-full flex items-center gap-3 py-2 text-left hover:bg-muted/40 -mx-2 px-2 rounded"
                      data-testid={`child-row-${it.id}`}>
                      {f.sequence != null && <span className="font-mono text-[11px] text-muted-foreground w-6 shrink-0">#{f.sequence}</span>}
                      <span className="flex-1 min-w-0 truncate text-sm">{it.title || it.record_number}</span>
                      {cl && cl.length > 0 && (
                        <span className="flex items-center gap-1.5 shrink-0">
                          <span className="w-12 h-1.5 rounded-full bg-muted overflow-hidden hidden sm:inline-block">
                            <span className="h-full block rounded-full bg-primary" style={{ width: `${(done / cl.length) * 100}%` }} />
                          </span>
                          <span className="text-[11px] text-muted-foreground tabular-nums">{done}/{cl.length}</span>
                        </span>
                      )}
                      {overdue && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium shrink-0">Overdue</span>}
                      {f.status && <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${STATUS_STYLE(f.status)}`}>{f.status}</span>}
                      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border">
                <Plus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <input
                  value={adding[grp.rel_def_id] || ""}
                  onChange={(e) => setAdding((a) => ({ ...a, [grp.rel_def_id]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChild(grp); } }}
                  placeholder={`Add ${(grp.target_entity_type_name || "item").replace(/s$/, "").toLowerCase()}…`}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  data-testid={`child-add-${grp.target_entity_type_id}`}
                />
                {(adding[grp.rel_def_id] || "").trim() && (
                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => addChild(grp)}>Add</Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
